
import { defineComponent, onMounted, inject, ref, watchEffect, onUnmounted } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { GET_USERS, SEARCH_USERS } from '../graphql/Queries';
import { DELETE_USER } from '../graphql/Mutations';
import { useStore } from 'vuex';
import Search from '../../../../components/search/Search.vue';
import { useI18n } from 'vue-i18n';
import Table from '../../../../components/Table/Table.vue';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import InnerLoader from '../../../../components/InnerLoader.vue';
import { dropdown_handler } from '../../../../core/helpers/dropdownHandler';

export default defineComponent({
    name: 'users-list',
    components: { Search, Table, InnerLoader },
    props: {
        widgetClasses: String
    },
    setup() {
        const i18n = useI18n();
        const checked = ref(false);
        const emitter: any = inject('emitter');
        const store = useStore();
        const result: any = ref([]);
        const pagination = ref({}) as Record<any, any>;
        const current_user = store.getters.getUser as Record<any, any>;
        const loader = ref(false);
        const loading = ref(false);
        const clickHandler = ref() as Record<any, any>;
        const currentPage = ref();
        const system_locale = ref();
        const activeIndex = ref(-1);
        const searchObservable: any = ref(null);
        const observable: any = ref(null);
        const columns = ref([
            {
                label: '',
                key: 'avatar',
                flex_start: false
            },
            {
                label: 'message.NAME',
                key: 'name',
                flex_start: true
            },
            {
                label: 'message.EMAIL',
                key: 'email',
                flex_start: true
            },
            {
                label: 'message.ROLE',
                key: 'roles',
                flex_start: true
            },
            {
                label: 'message.STATUS',
                key: 'status',
                flex_start: false
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        const filterObject = ref([
            {
                label: 'message.NAME',
                value: 'name'
            },
            {
                label: 'message.EMAIL',
                value: 'email'
            }
        ]);

        const users = (page = 1, reload = false) => {
            if (searchObservable.value != null) {
                searchObservable.value?.unsubscribe();
                searchObservable.value = null;
            }
            if (page == 1) observable.value?.unsubscribe();
            loader.value = true;
            observable.value = Apollo.watchQuery({
                query: GET_USERS,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-and-network',
                errorPolicy: 'all',
                variables: {
                    page: page,
                    limit: 10
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                reload == true ? (currentPage.value = 1) : false;
                clickHandler.value = users;
                result.value = data.users.data;
                pagination.value = data.users;
                loader.value = false;
            });
        };

        // const dropdownHandler = async (activeIndexValue, index) => {
        // 	const activeValue = await dropdown_handler(activeIndexValue, index);
        // 	activeIndex.value = activeValue;
        // };

        const dropdownHandler = (index, id) => {
            activeIndex.value = index === id ? null : id;
        };
        const handleClickOutside = event => {
            if (!event.target.closest('.action-btn')) {
                activeIndex.value = 1;
            }
        };

        onMounted(() => {
            users();
            document.addEventListener('click', handleClickOutside);
        });

        const handleCurrentChange = (val: number) => {
            clickHandler.value(val);
        };

        const searchHandler = (page = 1) => {
            if (observable.value != null) {
                observable.value?.unsubscribe();
                observable.value = null;
            }
            searchObservable.value?.unsubscribe();
            const searchData = store.getters.getSearchData;
            loader.value = true;
            searchObservable.value = Apollo.watchQuery({
                query: SEARCH_USERS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'network-only',
                variables: {
                    page: page,
                    limit: 10,
                    filter: searchData.data.select,
                    search_key: searchData.data.input,
                    locale: system_locale.value
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }

                clickHandler.value = searchHandler;
                result.value = data.search_users.data;
                pagination.value = data.search_users;
                loader.value = false;
            });
        };

        const handleDelete = (id: Record<any, any>) => {
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_USER,
                        variables: { id: id },
                        update: (store, { data: { delete_user } }) => {
                            if (searchObservable.value != null) searchHandler(pagination.value.current_page);
                            else users(pagination.value.current_page);
                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        const handleEdit = (user: Record<any, any>) => {
            emitter.emit('editUserActions', user);
        };

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        const exportFile = data => {
            const searchData = store.getters.getSearchData;
            loading.value = true;
            const sub = Apollo.watchQuery({
                query: SEARCH_USERS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    page: 0,
                    limit: 1,
                    filter: searchData?.data?.select ? searchData?.data?.select : '',
                    search_key: searchData?.data?.input ? searchData?.data?.input : '',
                    locale: system_locale.value,
                    type: data?.type,
                    date_from: data?.dateFrom,
                    date_to: data?.dateTo
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loading.value = false;
                } else if (data) {
                    window.open(data?.search_users?.data[0]?.path, '_blank');
                    loading.value = false;
                }

                loading.value = false;
                sub.unsubscribe();
            });
        };

        const formatRole = (role?: string | null) => {
            if (!role) {
                return '';
            }
            return role.replace(/_/g, ' ');
        };

        onUnmounted(() => {
            observable.value?.unsubscribe();
            searchObservable.value?.unsubscribe();
        });

        return {
            current_user,
            checked,
            result,
            pagination,
            currentPage,
            filterObject,
            columns,
            loading,
            loader,
            exportFile,
            handleCurrentChange,
            generateAvatar,
            searchHandler,
            users,
            handleEdit,
            handleDelete,
            activeIndex,
            formatRole,
            dropdownHandler,
            handleClickOutside
        };
    }
});
